class CrossSellManager {
  constructor() {
    this.containers = [];
    this.analytics = [];
    this.cart = document.querySelector('cart-notification') ?? document.querySelector('cart-drawer');
    this.config = {
      trackingEnabled: true,
      debug: false
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.containers = document.querySelectorAll('.cross-sell__wrapper');
    if (!this.containers.length) return;
    this.containers.forEach(container => this.initContainer(container));
    this.setupGlobalListeners();
  }

  initContainer(container) {
    this.setupNavigation(container);
    this.setupAddToCart(container);
    this.trackImpression(container);
  }

  setupNavigation(container) {
    const navButtons = container.querySelectorAll('.cross-sell__nav-btn');
    const productsContainer = container.querySelector('.cross-sell__products');

    if (!productsContainer || !navButtons.length) return;

    navButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.navigate(productsContainer, button.dataset.direction);
      });
    });

    setTimeout(() => this.updateNavigation(container), 100);
    productsContainer.addEventListener('scroll', () => this.updateNavigation(container));
  }

  navigate(productsContainer, direction) {
    const scrollAmount = productsContainer.clientWidth * 0.8;
    const currentScroll = productsContainer.scrollLeft;
    const newScroll = direction === 'next' 
      ? currentScroll + scrollAmount 
      : currentScroll - scrollAmount;

    productsContainer.scrollTo({
      left: Math.max(0, newScroll),
      behavior: 'smooth'
    });
  }

  updateNavigation(container) {
    const productsContainer = container.querySelector('.cross-sell__products');
    const prevButton = container.querySelector('.cross-sell__nav-btn--prev');
    const nextButton = container.querySelector('.cross-sell__nav-btn--next');

    if (!productsContainer || !prevButton || !nextButton) return;

    const { scrollLeft, scrollWidth, clientWidth } = productsContainer;
    const cards = productsContainer.querySelectorAll('.cross-sell__card');

    if (this.config.debug) {
      console.log('[CrossSell] Navigation update:', {
        scrollLeft,
        scrollWidth,
        clientWidth,
        cardCount: cards.length,
        canScrollLeft: scrollLeft > 5,
        canScrollRight: scrollLeft < scrollWidth - clientWidth - 10
      });
    }

    prevButton.disabled = scrollLeft <= 5;
    nextButton.disabled = scrollLeft >= scrollWidth - clientWidth - 10 || cards.length <= 1;

    if (cards.length > 1 && prevButton.disabled && nextButton.disabled) {
      nextButton.disabled = false;
      if (this.config.debug) {
        console.log('[CrossSell] Force enabled next button - cards available but scroll not detected');
      }
    }
  }

  setupAddToCart(container) {
    container.addEventListener('click', async (e) => {
      const addButton = e.target.closest('[data-cross-sell-action="add-to-cart"]');
      if (addButton) {
        e.preventDefault();
        await this.handleAddToCart(addButton);
        return;
      }

      const configureButton = e.target.closest('[data-cross-sell-action="configure"]');
      if (configureButton) {
        e.preventDefault();
        await this.handleConfigure(configureButton);
        return;
      }
    });
  }

  async handleAddToCart(button) {
    const wrapper = button.closest('.cross-sell__btn-wrapper');
    const variantId = button.dataset.variantId ?? wrapper?.dataset.variantId;

    if (!variantId) {
      console.error('[CrossSell] No variant ID provided');
      return;
    }

    button.setAttribute('aria-disabled', true);
    button.classList.add('cross-sell__btn--loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    if (this.cart?.getSectionsToRender) {
      const sections = this.cart.getSectionsToRender().map(section => section.id);
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement?.(document.activeElement);
    }

    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const result = await response.json();

      if (result.status) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cross-sell',
            productVariantId: variantId,
            errors: result.errors ?? result.description,
            message: result.message,
          });
        }
        console.error('[CrossSell] Add to cart error:', result.description);
        return;
      } else if (!this.cart) {
        window.location = window.routes.cart_url;
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cross-sell',
          productVariantId: variantId,
          cartData: result,
        });
      }

      this.cart?.renderContents?.(result);
      this.cart?.classList.remove('is-empty');
      this.trackAddToCart(button, result);

    } catch (error) {
      console.error('[CrossSell] Add to cart failed:', error);
    } finally {
      button.classList.remove('cross-sell__btn--loading');
      button.removeAttribute('aria-disabled');
      this.cart?.classList.remove('is-empty');
    }
  }

  async handleConfigure(button) {
    const drawer = document.querySelector('cross-sell-drawer');
    if (!drawer) {
      console.error('[CrossSell] Cross-sell drawer not found');
      return;
    }

    drawer.open();
    this.trackDrawerOpen();
  }

  trackDrawerOpen() {
    if (!this.config.trackingEnabled) return;

    this.analytics.push({
      event: 'cross_sell_drawer_opened',
      timestamp: Date.now()
    });

    if (this.config.debug) {
      console.log('[CrossSell] Drawer opened');
    }
  }

  setupGlobalListeners() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-cross-sell-link]');
      if (link) {
        this.trackClick(link);
      }

      const viewMoreBtn = e.target.closest('[data-cross-sell-action="view-more"]');
      if (viewMoreBtn) {
        this.handleViewMore(viewMoreBtn);
      }
    });
  }

  handleViewMore(button) {
    const drawer = document.querySelector('cross-sell-drawer');
    if (!drawer) {
      console.error('[CrossSell] Cross-sell drawer not found');
      return;
    }

    drawer.open();

    if (this.config.trackingEnabled) {
      this.analytics.push({
        event: 'cross_sell_view_more_clicked',
        timestamp: Date.now()
      });
    }
  }

  trackImpression(container) {
    if (!this.config.trackingEnabled) return;

    const productId = container.dataset.productId;

    this.analytics.push({
      event: 'cross_sell_impression',
      product_id: productId,
      timestamp: Date.now()
    });

    if (typeof gtag !== 'undefined') {
      gtag('event', 'view_item_list', {
        item_list_name: 'cross_sell_recommendations',
        item_list_id: `cross_sell_${productId}`
      });
    }
  }

  trackClick(element) {
    if (!this.config.trackingEnabled) return;

    const card = element.closest('.cross-sell__card');
    if (!card) return;

    const analyticsData = this.getAnalyticsData(card);
    if (!analyticsData) return;

    this.analytics.push({
      event: 'cross_sell_click',
      ...analyticsData,
      click_type: element.dataset.crossSellLink ?? 'unknown'
    });

    console.log('[CrossSell] Tracked click:', analyticsData);
  }

  trackAddToCart(button, cartResult) {
    if (!this.config.trackingEnabled) return;

    const card = button.closest('.cross-sell__card');
    if (!card) return;

    const analyticsData = this.getAnalyticsData(card);
    if (!analyticsData) return;

    this.analytics.push({
      event: 'cross_sell_add_to_cart',
      ...analyticsData,
      cart_result: cartResult
    });

    if (typeof gtag !== 'undefined') {
      gtag('event', 'add_to_cart', {
        currency: 'PLN',
        value: analyticsData.productPrice / 100,
        items: [{
          item_id: analyticsData.productId,
          item_name: analyticsData.productTitle,
          item_category: 'cross_sell',
          price: analyticsData.productPrice / 100,
          quantity: 1
        }]
      });
    }

    console.log('[CrossSell] Tracked add to cart:', analyticsData);
  }

  getAnalyticsData(card) {
    const dataScript = card.querySelector('.cross-sell__analytics-data');
    if (!dataScript) return null;

    try {
      return JSON.parse(dataScript.textContent);
    } catch (error) {
      console.warn('[CrossSell] Failed to parse analytics data:', error);
      return null;
    }
  }

}

window.CrossSellManager = new CrossSellManager();

if (typeof module !== 'undefined' && module.exports) {
  module.exports = CrossSellManager;
}
